import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";

// Module-level cache so multiple instances on the same page don't re-fetch.
let _partnersCache = null;
let _partnersPromise = null;
const _subs = new Set();

function loadPartners(force = false) {
    if (force) { _partnersCache = null; _partnersPromise = null; }
    if (_partnersCache) return Promise.resolve(_partnersCache);
    if (_partnersPromise) return _partnersPromise;
    _partnersPromise = api.get("/partners").then((r) => {
        _partnersCache = r.data || [];
        _subs.forEach((fn) => fn(_partnersCache));
        return _partnersCache;
    }).catch(() => { _partnersCache = []; return []; });
    return _partnersPromise;
}

export function refreshPartnerCache() { return loadPartners(true); }

/**
 * Type-search partner picker.
 *
 * Two modes:
 *   - valueMode="name" (default): `value` is partner_name string, free-typing allowed.
 *   - valueMode="id":              `value` is partner_id; selection must come from the list.
 *                                  onChange receives (id, partner_obj). Display shows partner name.
 *
 * Props:
 *   value, onChange, placeholder, testId, disabled, valueMode, filter (optional partners filter fn)
 */
export function PartnerAutocomplete({
    value,
    onChange,
    placeholder = "Type to search…",
    testId,
    disabled = false,
    valueMode = "name",
    filter,
}) {
    const [partners, setPartners] = useState(_partnersCache || []);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const [draft, setDraft] = useState(""); // input text in id mode while user is typing
    const [editing, setEditing] = useState(false); // in id mode: true while typing/searching
    const wrapRef = useRef(null);

    useEffect(() => {
        const sub = (list) => setPartners(list);
        _subs.add(sub);
        loadPartners().then(sub);
        return () => { _subs.delete(sub); };
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setEditing(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const list = filter ? partners.filter(filter) : partners;

    // Compute display text in input
    const selectedPartner = useMemo(() => {
        if (valueMode === "id") return list.find((p) => p.id === value) || null;
        return null;
    }, [valueMode, value, list]);

    const inputText = valueMode === "id"
        ? (editing ? draft : (selectedPartner?.name || ""))
        : (value || "");

    const q = inputText.toLowerCase().trim();
    const matches = !q
        ? list.slice(0, 50)
        : (() => {
            const starts = list.filter((p) => p.name.toLowerCase().startsWith(q));
            const incl = list.filter((p) => !p.name.toLowerCase().startsWith(q) && p.name.toLowerCase().includes(q));
            return [...starts, ...incl].slice(0, 50);
        })();

    const pick = (p) => {
        if (valueMode === "id") {
            onChange(p.id, p);
            setDraft(p.name);
            setEditing(false);
        } else {
            onChange(p.name);
        }
        setOpen(false);
    };

    const handleChange = (e) => {
        const v = e.target.value;
        if (valueMode === "id") {
            setDraft(v);
            setEditing(true);
            // Clear selection while user is typing
            if (value) onChange("", null);
        } else {
            onChange(v);
        }
        setOpen(true);
        setHighlight(0);
    };

    const handleKey = (e) => {
        if (!open) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, matches.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
        else if (e.key === "Enter" && matches[highlight]) { e.preventDefault(); pick(matches[highlight]); }
        else if (e.key === "Escape") { setOpen(false); if (valueMode === "id") setEditing(false); }
    };

    return (
        <div ref={wrapRef} className="relative">
            <input
                value={inputText}
                onChange={handleChange}
                onFocus={() => {
                    if (disabled) return;
                    setOpen(true);
                    if (valueMode === "id") {
                        setDraft(selectedPartner?.name || "");
                        setEditing(true);
                    }
                }}
                onKeyDown={handleKey}
                placeholder={placeholder}
                autoComplete="off"
                disabled={disabled}
                className="flex h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] disabled:bg-slate-50 disabled:text-slate-400"
                data-testid={testId}
            />
            {open && !disabled && matches.length > 0 && (
                <div
                    className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white border border-slate-200 rounded-sm shadow-lg"
                    data-testid={testId ? `${testId}-list` : undefined}
                >
                    {matches.map((p, i) => (
                        <button
                            key={p.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                            onMouseEnter={() => setHighlight(i)}
                            className={`block w-full text-left px-3 py-1.5 text-[13px] ${i === highlight ? "bg-[#002FA7]/10 text-[#002FA7]" : "hover:bg-slate-50 text-slate-800"}`}
                            data-testid={testId ? `${testId}-option-${i}` : undefined}
                        >
                            <span className="font-medium">{p.name}</span>
                            {p.country && <span className="ml-2 text-[11px] text-slate-500">— {p.country}</span>}
                            {p.segment && <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">{p.segment}</span>}
                        </button>
                    ))}
                </div>
            )}
            {open && !disabled && q && matches.length === 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg px-3 py-2 text-[12px] text-slate-500">
                    {valueMode === "id"
                        ? `No partner matches “${inputText}”. Create it on the Partners page first.`
                        : `No matching partner — press Enter to use “${inputText}” as-is.`}
                </div>
            )}
        </div>
    );
}
